export const getContainerState = (container) => {
  if (container.deleted_at !== null && container.deleted_at !== undefined) {
    return 'deleted'
  }

  return container.status ? 'active' : 'inactive'
}

const variationIds = (variations = []) => variations
  .map((variation) => variation && variation.id)
  .filter((id) => id !== null && id !== undefined)

export const buildContainerPayload = (container, status = container.status) => ({
  status: Boolean(status),
  valid_for: container.valid_for,
  email_template: container.email_template,
  pdf_template: container.pdf_template ?? null,
  pdf_title: container.pdf_title ?? null,
  pdf_link_text: container.pdf_link_text ?? null,
  product_id: container.product_id ?? container.product.id,
  variations: (container.product_variations ?? container.variations ?? []).map(({ id }) => ({ id })),
  batches: (container.batches ?? []).map((item) => ({
    batch: { id: item.batch.id },
    generate_barcodes: Boolean(item.generate_barcodes),
    code_prefix: item.code_prefix ?? null,
    code_suffix: item.code_suffix ?? null,
  })),
})

export const findOverlappingActiveContainers = (allContainers, variations, targetId = null) => {
  const selectedIds = new Set(variationIds(variations))

  return allContainers.filter((container) => {
    if (container.id === targetId || getContainerState(container) !== 'active') {
      return false
    }

    return variationIds(container.product_variations).some((id) => selectedIds.has(id))
  })
}

export const loadAllContainers = async (containersApi) => {
  const firstPage = await containersApi.list(1)
  const items = [...firstPage.data()]
  const remainingPages = []

  for (let page = 2; page <= firstPage.meta.last_page; page += 1) {
    remainingPages.push(containersApi.list(page))
  }

  const responses = await Promise.all(remainingPages)
  responses.forEach((response) => {
    items.push(...response.data())
  })

  return items
}

export const activateWithConflicts = async (containersApi, target, conflicts, targetPayload) => {
  const deactivatedIds = []

  try {
    for (const conflict of conflicts) {
      await containersApi.update(conflict.id, buildContainerPayload(conflict, false))
      deactivatedIds.push(conflict.id)
    }

    await containersApi.update(
      target.id,
      { ...(targetPayload ?? buildContainerPayload(target)), status: true },
    )
  } catch (error) {
    error.containerStatusProgress = {
      targetId: target.id,
      deactivatedIds,
    }
    throw error
  }

  return deactivatedIds
}

export const containerErrorMessage = (error) => {
  const errors = error && error.errors
  const messages = Array.isArray(errors)
    ? errors
    : Object.values(errors ?? {}).reduce((all, value) => (
      all.concat(Array.isArray(value) ? value : [value])
    ), [])

  return messages.filter(Boolean).join('; ') || error?.message || 'The container could not be updated.'
}

export const conflictMessage = (conflicts, variations) => {
  const selectedIds = new Set(variationIds(variations))
  const details = conflicts.map((container) => {
    const names = (container.product_variations ?? [])
      .filter((variation) => selectedIds.has(variation.id))
      .map((variation) => variation.variation_name ?? `#${variation.id}`)

    return `#${container.id}${names.length ? ` (${names.join(', ')})` : ''}`
  })

  return `Active containers ${details.join(', ')} use the same variations. They will be deactivated before this container is activated.`
}
